import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Text } from "../../../../components/Text";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { WAMetaMessageDirection } from "../../../../model/integration/types/whatsapp/WAMetaMessageDirection.enum";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";

const Quote = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 0;
  text-align: start;
  cursor: pointer;
  border: none;
  padding: ${Spacing.xs} ${Spacing.s};
  margin-bottom: ${Spacing.xs};
  border-radius: ${Rounded.sm};
  background-color: rgba(128, 128, 128, 0.18);
  border-inline-start: 3px solid currentColor;
  color: inherit;

  &:hover {
    background-color: rgba(128, 128, 128, 0.28);
  }
`;

const Author = styled(Text)`
  color: inherit;
`;

const Snippet = styled(Text)`
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  opacity: 0.85;
  unicode-bidi: plaintext;
  text-align: start;
`;

export const useQuotePreview = (message?: ConversationMessage) => {
  const { t } = useTranslation();

  const author = !message
    ? ""
    : message.direction === WAMetaMessageDirection.OUTGOING
      ? t("integration.whatsapp.meta.reply.you")
      : t("integration.whatsapp.meta.reply.customer");

  const preview = !message
    ? t("integration.whatsapp.meta.reply.unavailable")
    : message.deletedAt
      ? t("common.whatsapp.deleted")
      : message.type === "text"
        ? message.text?.body || ""
        : t(`common.whatsapp.${message.type}`, message.type);

  return { author, preview };
};

type Props = {
  repliedTo?: ConversationMessage;
  onJump: VoidFunction;
  /** The thread can fetch the original if it is not loaded, so the quote stays
   * clickable rather than looking broken.
   */
  jumpable?: boolean;
};

export const WAMessageReply: React.FC<Props> = ({
  repliedTo,
  onJump,
  jumpable,
}) => {
  const { author, preview } = useQuotePreview(repliedTo);

  return (
    <Quote
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onJump();
      }}
      disabled={!repliedTo && !jumpable}
      title={preview}
    >
      {author ? (
        <Author fontSize="xs" fontWeight="bold">
          {author}
        </Author>
      ) : null}
      <Snippet fontSize="s">{preview}</Snippet>
    </Quote>
  );
};
